<template>
    <AiSummaryWidgetWrapper
        :widget-data="props.data"
        :has-content="items.length > 0"
        :call-count="callCount"
        :included-call-count="includedCallCount"
        :skipped-call-count="skippedCallCount"
        :analysis-progress="analysisProgress"
        :is-regenerating="isRegenerating"
        :is-exporting="isExporting"
        :is-downloading-md="isDownloadingMd"
        wrapper-class="summary-renderer py-2 px-2"
        @regenerate="onRegenerateSummary"
        @export-docx="onExportToDocx"
        @download-md="onDownloadMarkdown"
    >
        <AiSummaryList
            variant="numbered"
            :items="items"
        />
    </AiSummaryWidgetWrapper>
</template>

<script setup lang="ts">
//TODO CHECK STYLES AND UI AFTER MIGRATION
import { computed, ref } from 'vue'
import { AISummaryResponse, WidgetExtended } from '@/types/dashboard'
import useNextUpdatedWidgetData from '@/composables/useNextUpdatedWidgetData'
import AiSummaryWidgetWrapper from '@/components/RedesignComponents/Widgets/Common/AiSummaryWidgetWrapper.vue'
import AiSummaryList from '@/components/RedesignComponents/Widgets/Common/AiSummaryList.vue'
import { useAiSummaryProgress } from '@/composables/useAiSummaryProgress'
import { regenerateAiSummary, exportMarkdownToDocx, downloadMarkdownFile } from '@/helpers/aiSummaryHelpers'

interface NumberItem {
    label: string
    value: string | number
}

/* -------------------------------------
 * Props
 * ----------------------------------- */
const props = defineProps<{
    data: WidgetExtended
}>()

const { data, run } = useNextUpdatedWidgetData<
    AISummaryResponse,
    typeof props,
    'data'
>(props, 'data')

/* -------------------------------------
 * Loading states
 * ----------------------------------- */
const isRegenerating = ref(false)
const isExporting = ref(false)
const isDownloadingMd = ref(false)

/* -------------------------------------
 * Computed parsing logic - Numbered list specific
 * ----------------------------------- */
const items = computed<NumberItem[]>(() => {
    const raw = data?.value?.SummaryText
    if (!raw) return []
    try {
        const parsed = JSON.parse(data?.value?.SummaryText)
        return Array.isArray(parsed?.items) ? parsed.items : []
    } catch (e) {
        console.warn('Invalid SummaryText JSON', e)
        return []
    }
})

/* -------------------------------------
 * Generate markdown numbered list
 * ----------------------------------- */
const markdownContent = computed(() => {
    if (!items.value.length) return ''
    return items.value
        .map((item, index) => `${index + 1}. ${item?.label} - **${item?.value}**`)
        .join('\n')
})

/* -------------------------------------
 * Use progress composable
 * ----------------------------------- */
const { callCount, includedCallCount, skippedCallCount, analysisProgress } =
    useAiSummaryProgress(data)

/* -------------------------------------
 * Action handlers
 * ----------------------------------- */
async function onRegenerateSummary () {
    if (!props.data.WidgetID || isRegenerating.value) return

    isRegenerating.value = true
    try {
        await regenerateAiSummary(props.data.WidgetID, run)
    } catch (error) {
        console.error('Failed to regenerate summary:', error)
    } finally {
        isRegenerating.value = false
    }
}

async function onExportToDocx () {
    if (!markdownContent.value || isExporting.value) return

    isExporting.value = true
    try {
        await exportMarkdownToDocx(markdownContent.value, props.data)
    } catch (error) {
        console.error('Failed to export to DOCX:', error)
    } finally {
        isExporting.value = false
    }
}

function onDownloadMarkdown () {
    if (!markdownContent.value || isDownloadingMd.value) return

    isDownloadingMd.value = true
    try {
        downloadMarkdownFile(markdownContent.value, props.data)
    } catch (error) {
        console.error('Failed to download markdown:', error)
    } finally {
        isDownloadingMd.value = false
    }
}
</script>
